import type { ColumnType, ChartFieldRole, ChartValidationResult } from '../types/columnType';

/**
 * Static mapping of chart field roles to compatible column types.
 */
const ROLE_COMPATIBILITY: Record<ChartFieldRole, ColumnType[]> = {
  category: ['Text', 'Boolean'],
  numeric: ['Number', 'Percentage', 'Currency'],
  temporal: ['Date', 'DateTime'],
  any: ['Text', 'Number', 'Date', 'DateTime', 'Percentage', 'Currency', 'Boolean', 'URL'],
};

/**
 * Check if a column type is compatible with a chart field role.
 */
export function isTypeCompatibleWithRole(
  columnType: ColumnType,
  role: ChartFieldRole
): boolean {
  return ROLE_COMPATIBILITY[role].includes(columnType);
}

export interface ChartFieldMapping {
  fieldName: string;
  columnIndex: number;
  requiredRole: ChartFieldRole;
}

export interface ChartConfig {
  chartId: string;
  chartType: string;
  fields: ChartFieldMapping[];
}

/**
 * Validate a single chart configuration against current column types.
 * Returns validation results (errors/warnings/suggestions).
 */
export function validateChart(
  chart: ChartConfig,
  columnTypes: ColumnType[]
): ChartValidationResult[] {
  const results: ChartValidationResult[] = [];

  for (const field of chart.fields) {
    const actualType = columnTypes[field.columnIndex];
    if (actualType === undefined) continue;

    if (!isTypeCompatibleWithRole(actualType, field.requiredRole)) {
      const compatibleTypes = ROLE_COMPATIBILITY[field.requiredRole].join(', ');
      results.push({
        chartId: chart.chartId,
        severity: 'error',
        message: `Field "${field.fieldName}" requires a ${field.requiredRole} type (${compatibleTypes}), but column is "${actualType}".`,
        affectedField: field.fieldName,
        requiredRole: field.requiredRole,
        actualType,
      });
    }
  }

  return results;
}

/**
 * Validate all chart configurations against current column types.
 * Returns all validation results across all charts.
 */
export function validateChartFields(
  charts: ChartConfig[],
  columnTypes: ColumnType[]
): ChartValidationResult[] {
  const results: ChartValidationResult[] = [];

  for (const chart of charts) {
    results.push(...validateChart(chart, columnTypes));
  }

  return results;
}
